/**
 * 送信の履歴と結果: 一覧（GET …/mail/broadcasts）と詳細（GET …/broadcasts/:id・右のドロワー）。
 * 詳細: 宛先数・送れた・宛先不明・苦情・停止（+ 0 でなければ 送れなかった・取り消した・送信待ち）。
 * 下書き・予約・送信中は取り消せる（DELETE）。下書きは送り直せる（POST …/send）。
 *
 * 送り始めの 1 日の上限（warmup・2026-09-27 creator/mail-warmup）: 送信中で、今日の上限に達して翌日に回った行があれば、一覧に
 *   「翌日に回った {pending} 通」、詳細にその数と続きを送り始める時刻（現地時刻）を出す（判定は mailWarmup.carriedOver）。
 *
 * WHY 率を自動停止の線と並べるか: 苦情 0.1% / 宛先不明 5% を超えると開発者ごと止まり、解除は運営の確認になる（D11）。
 *   止まってから知るより、1 回ごとの結果で「そろそろ危ない」に気づけるようにする（線の半分で注意の色）。
 */
import { useEffect, useState } from 'react';
import { api, ApiError, newIdempotencyKey } from '../../api';
import { ConfirmDialog, Drawer, Empty, ErrorState, fullDate, num, Spinner } from '../../components/ui';
import { useResource } from '../../hooks';
import { formatPercent, useT, type MessageKey } from '../../i18n';
import type { Broadcast } from '../../mailApi';
import { canCancel, canSendDraft, rateOf, rateTone, statusTone, STOP_LINES, unwrap } from '../../mailLogic';
import { carriedOver, limitKind, resumeAt, type MailWarmup } from '../../mailWarmup';
import { everyLabel, failureText, mailErrorText, statusLabel } from './mailDisplay';

const toneClass = (tone: string) => (tone === 'none' ? '' : `pill-${tone}`);

/** 一覧の「いつ」: 送った時刻 > 定期の次回 > 予約の時刻 > 作った時刻 */
function whenOf(b: Broadcast): string | null {
  return b.sentAt ?? b.nextRunAt ?? b.sendAt ?? b.createdAt ?? null;
}

export function HistoryCard({ mail, tick, warmup, onChanged }: { mail: string; tick: number; warmup: MailWarmup | null; onChanged: () => void }) {
  const { t } = useT();
  const list = useResource<unknown>(`${mail}/broadcasts`);
  const [openId, setOpenId] = useState<string | null>(null);
  // 送った・取り消した（tick が変わった）ら読み直す。初回は useResource が読むので 0 のときは何もしない。
  // 依存は tick だけ（reload は useCallback で安定・list 全体を入れると読み込みのたびに走る）
  useEffect(() => {
    if (tick) list.reload();
  }, [tick]);

  const rows = (list.data ? unwrap<Broadcast[]>(list.data, 'broadcasts') : null) ?? [];

  return (
    <section className="card mail-history" aria-labelledby="mail-history-title">
      <div className="card-head">
        <h2 className="card-title" id="mail-history-title">
          {t('mail.history.title')}
        </h2>
        <button type="button" className="btn btn-ghost btn-sm" onClick={list.reload}>
          {t('mail.history.refresh')}
        </button>
      </div>
      {list.loading && !list.data && <Spinner />}
      {list.error && <ErrorState error={list.error} what={t('mail.history.title')} onRetry={list.reload} />}
      {list.data != null && rows.length === 0 && <Empty title={t('mail.history.empty')}>{t('mail.history.emptyBody')}</Empty>}
      {rows.length > 0 && (
        <ul className="mail-list">
          {rows.map((b) => {
            const when = whenOf(b);
            const carried = carriedOver(b, warmup);
            return (
              <li key={b.id}>
                <button type="button" className="mail-row" onClick={() => setOpenId(b.id)}>
                  <span className="mail-row-main">
                    <strong className="mail-row-subject">{b.subject}</strong>
                    <span className="small muted">
                      {b.recurrence ? `${everyLabel(b.recurrence.every, b.recurrence.at, b.sendAt)} · ` : ''}
                      {when ? fullDate(when) : '—'}
                    </span>
                  </span>
                  <span className="mail-row-side">
                    <span className={`pill ${toneClass(statusTone(b.status))}`}>{statusLabel(b.status)}</span>
                    {b.counts?.recipients != null && (
                      <span className="small muted">{t('mail.history.sentOf', { sent: num(b.counts.sent ?? 0), recipients: num(b.counts.recipients) })}</span>
                    )}
                    {carried > 0 && <span className="small muted">{t('mail.history.carriedOver', { count: carried })}</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <BroadcastDrawer
        mail={mail}
        id={openId}
        warmup={warmup}
        onClose={() => setOpenId(null)}
        onChanged={() => {
          list.reload();
          onChanged();
        }}
      />
    </section>
  );
}

function BroadcastDrawer({ mail, id, warmup, onClose, onChanged }: { mail: string; id: string | null; warmup: MailWarmup | null; onClose: () => void; onChanged: () => void }) {
  const { t } = useT();
  const r = useResource<unknown>(id ? `${mail}/broadcasts/${encodeURIComponent(id)}` : null);
  const b = r.data ? unwrap<Broadcast>(r.data, 'broadcast') : null;
  const [action, setAction] = useState<'cancel' | 'send' | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  // 下書きを送る操作の Idempotency-Key（確認を開くたびに 1 つ）
  const [key, setKey] = useState('');

  useEffect(() => {
    setErr(null);
    setAction(null);
  }, [id]);

  const run = async () => {
    if (!b || !action) return;
    setBusy(true);
    setErr(null);
    try {
      if (action === 'cancel') await api(`${mail}/broadcasts/${encodeURIComponent(b.id)}`, { method: 'DELETE' });
      else await api(`${mail}/broadcasts/${encodeURIComponent(b.id)}/send`, { method: 'POST', body: {}, headers: { 'idempotency-key': key } });
      setAction(null);
      r.reload();
      onChanged();
    } catch (e) {
      setErr(e as ApiError);
      setAction(null);
    } finally {
      setBusy(false);
    }
  };

  const c = b?.counts ?? {};
  const bounceRate = rateOf(c.bounced, c.sent);
  const complaintRate = rateOf(c.complained, c.sent);
  const rateCell = (rate: number | null, line: number) =>
    rate == null ? null : <span className={`pill ${toneClass(rateTone(rate, line))}`}>{formatPercent(rate, 2)}</span>;
  const filterText = (f: Broadcast['filters']) => {
    const parts: string[] = [];
    if (f?.language) parts.push(t('mail.detail.filterLanguage', { lang: f.language }));
    if (f?.lastPlayedWithinDays) parts.push(t('mail.audience.withinDays', { count: f.lastPlayedWithinDays }));
    return parts.length ? parts.join(' · ') : t('mail.detail.noFilter');
  };

  return (
    <Drawer open={!!id} title={b?.subject ?? t('mail.history.title')} onClose={onClose}>
      {r.loading && !b && <Spinner />}
      {r.error && <ErrorState error={r.error} what={t('mail.history.title')} onRetry={r.reload} />}
      {b && (
        <div className="mail-detail">
          <p>
            <span className={`pill ${toneClass(statusTone(b.status))}`}>{statusLabel(b.status)}</span>
            {b.failureReason && <span className="small muted"> {failureText(b.failureReason, b)}</span>}
          </p>

          <section className="tiles mail-tiles" aria-label={t('mail.detail.results')}>
            {(
              [
                ['recipients', 'mail.detail.recipients'],
                ['sent', 'mail.detail.sent'],
                ['bounced', 'mail.detail.bounced'],
                ['complained', 'mail.detail.complained'],
                ['unsubscribed', 'mail.detail.unsubscribed'],
                ['failed', 'mail.detail.failed'],
                ['canceled', 'mail.detail.canceled'],
                ['pending', 'mail.detail.pending']
              ] as Array<[keyof NonNullable<Broadcast['counts']>, MessageKey]>
            )
              // 内訳の補足（送れなかった・取り消した・送信待ち）は 0 のとき出さない: ふだんは 0 で、並べると結果の本体（送れた・苦情）が埋もれる
              .filter(([k]) => !(k === 'failed' || k === 'canceled' || k === 'pending') || (c[k] ?? 0) > 0)
              .map(([k, label]) => (
                <div className="tile" key={k}>
                  <span className="tile-label">{t(label)}</span>
                  <span className="tile-value">{c[k] == null ? '—' : num(c[k]!)}</span>
                  {k === 'bounced' && rateCell(bounceRate, STOP_LINES.bounce)}
                  {k === 'complained' && rateCell(complaintRate, STOP_LINES.complaint)}
                </div>
              ))}
          </section>
          {b && carriedOver(b, warmup) > 0 && (
            <p className="alert alert-info" role="note">
              {/* 本人確認が済むまでの上限なら「送り始めの」と書かない（mailWarmup.limitKind・creator/mail-identity） */}
              {t(limitKind(warmup) === 'identity' ? 'mail.detail.carriedOverIdentity' : 'mail.detail.carriedOver', { count: carriedOver(b, warmup), at: resumeAt(warmup) ? fullDate(resumeAt(warmup)!) : '—' })}
            </p>
          )}
          <p className="small muted">{t('mail.detail.lines', { complaint: formatPercent(STOP_LINES.complaint, 1), bounce: formatPercent(STOP_LINES.bounce, 0) })}</p>

          <dl className="kv">
            {b.recurrence && (
              <>
                <dt>{t('mail.detail.recurrence')}</dt>
                <dd>{everyLabel(b.recurrence.every, b.recurrence.at, b.sendAt)}</dd>
              </>
            )}
            {b.nextRunAt && (
              <>
                <dt>{t('mail.detail.nextRun')}</dt>
                <dd>{fullDate(b.nextRunAt)}</dd>
              </>
            )}
            {b.sendAt && !b.recurrence && (
              <>
                <dt>{t('mail.detail.sendAt')}</dt>
                <dd>{fullDate(b.sendAt)}</dd>
              </>
            )}
            {b.sentAt && (
              <>
                <dt>{t('mail.detail.sentAt')}</dt>
                <dd>{fullDate(b.sentAt)}</dd>
              </>
            )}
            <dt>{t('mail.detail.audience')}</dt>
            <dd>{filterText(b.filters)}</dd>
            <dt>{t('mail.detail.createdAt')}</dt>
            <dd>{fullDate(b.createdAt)}</dd>
          </dl>
          <p className="small muted">{t('mail.detail.localTime')}</p>

          {b.text && <div className="mail-preview-body mail-pre">{b.text}</div>}

          {err && (
            <p className="alert alert-error" role="alert">
              {mailErrorText(err)}
            </p>
          )}
          <div className="form-actions">
            {canSendDraft(b.status) && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setKey(newIdempotencyKey());
                  setAction('send');
                }}
              >
                {t('mail.detail.sendDraft')}
              </button>
            )}
            {canCancel(b.status) && (
              <button type="button" className="btn btn-danger" onClick={() => setAction('cancel')}>
                {b.status === 'sending' ? t('mail.detail.stop') : t('mail.detail.cancel')}
              </button>
            )}
          </div>
        </div>
      )}
      <ConfirmDialog
        open={!!action}
        title={action === 'send' ? t('mail.confirm.title.now') : b?.status === 'sending' ? t('mail.detail.stopTitle') : t('mail.detail.cancelTitle')}
        confirmLabel={action === 'send' ? t('mail.confirm.go.now') : b?.status === 'sending' ? t('mail.detail.stop') : t('mail.detail.cancel')}
        tone={action === 'send' ? 'default' : 'danger'}
        busy={busy}
        onConfirm={run}
        onClose={() => !busy && setAction(null)}
      >
        {b && (
          <>
            <p>
              <strong>{b.subject}</strong>
            </p>
            <p className="small muted">
              {action === 'send' ? t('mail.detail.sendDraftBody') : b.status === 'sending' ? t('mail.detail.stopBody') : b.recurrence ? t('mail.detail.cancelRecurringBody') : t('mail.detail.cancelBody')}
            </p>
          </>
        )}
      </ConfirmDialog>
    </Drawer>
  );
}
